/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Menu,
    Alert,
} from "@mui/material";
import {
    ArrowBack,
    MailOutline,
    HistoryOutlined,
    CheckCircleOutline,
    ErrorOutline,
    BlockOutlined,
    SwapHorizOutlined,
    TimelineOutlined,
    MarkEmailReadOutlined,
    OpenInNewOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchInvoice,
    fetchInvoiceTimeline,
    fetchTemplates,
    sendManualReminder,
    fetchRecoveryStateMetadata,
    fetchInvoiceTransitions,
    transitionInvoiceState,
    fetchInvoicePromises,
    createInvoicePromise,
    markPromiseKept,
    markPromiseBroken,
    cancelInvoicePromise,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectCurrentInvoice,
    selectInvoicesError,
    selectInvoiceTimeline,
    selectIsLoadingTimeline,
    selectTemplates,
    selectIsSendingManualReminder,
    selectManualReminderError,
    selectRecoveryStateMetadata,
    selectInvoiceTransitions,
    selectIsLoadingInvoiceTransitions,
    selectIsTransitioningInvoiceState,
    selectInvoicePromises,
    selectIsLoadingInvoicePromises,
    selectIsSavingPromise,
    selectPromiseSaveError,
} from "../../redux/selectors/collectSelector";
import {
    IDunningEvent,
    IInvoiceTimelineEntry,
    IReminderTemplate,
    IRecoveryStateTransition,
    RecoveryState,
    IPromiseToPay,
    ICustomerReply,
    REPLY_INTENT_LABELS,
    REPLY_STATUS_LABELS,
} from "../../utility/types/collectTypes";
import { submitListReplies } from "../../redux/services/collectService";
import {
    fetchInvoiceActivity,
    IActivityItem,
} from "../../redux/services/collectTaskService";
import {
    recoveryStateLabel,
    recoveryStateColors,
    transitionSourceLabel,
} from "./recoveryStateHelpers";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectStatusChip,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";
import ChannelBadge from "./ChannelBadge";

function formatMoney(value: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
        }).format(value || 0);
    } catch {
        return `$${(value || 0).toFixed(2)}`;
    }
}

function formatDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        });
    } catch {
        return value;
    }
}

function formatDateTime(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
}

// Basic email shape check for the manual-reminder "Send to (override)" field.
// The override is used verbatim as the email recipient, so anything that
// isn't a valid address must be rejected before we hit the backend.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Rewrite legacy auto-promise notes that contain raw classifier metadata
 * (model name, heuristic_was breadcrumbs, "Reasons: llm:..." prefix) into
 * something a non-technical SMB operator can read. New promises already
 * arrive in friendly format from the backend — this is a safety net for
 * pre-existing rows.
 */
function humanizePromiseNote(note: string): string {
    if (!note) return note;
    const isLegacyAuto = /^Auto-created from customer reply/i.test(note);
    if (!isLegacyAuto) return note;

    let cleaned = note;
    // Drop the parenthetical with confidence + model name; keep just the %.
    const confMatch = cleaned.match(/confidence\s+([0-9.]+)/i);
    const confidencePct = confMatch
        ? Math.round(parseFloat(confMatch[1]) * 100)
        : null;

    // Pull the LLM rationale out of "Reasons: llm:<text>, heuristic_was:..."
    const reasonsIdx = cleaned.toLowerCase().indexOf("reasons:");
    let llmReason: string | null = null;
    if (reasonsIdx >= 0) {
        const rest = cleaned.slice(reasonsIdx + "reasons:".length);
        const llmMatch = rest.match(/llm:\s*([^,]*?)(?:,\s*heuristic_was:|$)/i);
        if (llmMatch) llmReason = llmMatch[1].trim().replace(/[.\s]+$/, "");
    }

    const parts: string[] = ["Auto-created from customer reply."];
    if (llmReason) parts.push(`AI read of the reply: ${llmReason}.`);
    if (confidencePct !== null) parts.push(`(${confidencePct}% confidence)`);
    return parts.join(" ");
}


const TIMELINE_ICONS: Record<string, React.ReactNode> = {
    sent: <CheckCircleOutline sx={{ color: "#0f7a5b" }} />,
    failed: <ErrorOutline sx={{ color: "#b91c1c" }} />,
    skipped: <BlockOutlined sx={{ color: "#6b7280" }} />,
};

const CollectInvoiceDetail: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();
    const { invoiceId } = useParams<{ invoiceId: string }>();

    const businessId = useSelector(selectActiveBusinessId);
    const invoice = useSelector(selectCurrentInvoice);
    const error = useSelector(selectInvoicesError);
    const timeline = useSelector(selectInvoiceTimeline);
    const isLoadingTimeline = useSelector(selectIsLoadingTimeline);
    const templates = useSelector(selectTemplates);
    const isSending = useSelector(selectIsSendingManualReminder);
    const sendError = useSelector(selectManualReminderError);
    const recoveryStateMetadata = useSelector(selectRecoveryStateMetadata);
    const invoiceTransitions = useSelector(selectInvoiceTransitions);
    const isLoadingTransitions = useSelector(selectIsLoadingInvoiceTransitions);
    const isTransitioning = useSelector(selectIsTransitioningInvoiceState);
    const promises = useSelector(selectInvoicePromises);
    const isLoadingPromises = useSelector(selectIsLoadingInvoicePromises);
    const isSavingPromise = useSelector(selectIsSavingPromise);
    const promiseSaveError = useSelector(selectPromiseSaveError);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [pickedTemplateId, setPickedTemplateId] = useState<string>("");
    const [recipientOverride, setRecipientOverride] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [stateMenuAnchor, setStateMenuAnchor] =
        useState<HTMLElement | null>(null);
    const [promiseDialogOpen, setPromiseDialogOpen] = useState(false);
    const [promiseAmount, setPromiseAmount] = useState<string>("");
    const [promiseDate, setPromiseDate] = useState<string>("");
    const [promiseNote, setPromiseNote] = useState<string>("");

    useEffect(() => {
        if (!businessId || !invoiceId) return;
        dispatch(fetchInvoice({ invoiceId }));
        dispatch(fetchInvoiceTimeline({ invoiceId }));
        dispatch(fetchInvoiceTransitions({ invoiceId }));
        dispatch(fetchInvoicePromises({ invoiceId }));
        if (templates.length === 0) {
            dispatch(fetchTemplates({}));
        }
        if (recoveryStateMetadata.length === 0) {
            dispatch(fetchRecoveryStateMetadata(undefined));
        }
    }, [
        dispatch,
        businessId,
        invoiceId,
        templates.length,
        recoveryStateMetadata.length,
    ]);

    const currentRecoveryState =
        (invoice?.recoveryState as RecoveryState | undefined) || undefined;
    const recoveryColors = recoveryStateColors(currentRecoveryState);
    const allowedTransitions = useMemo(() => {
        if (!currentRecoveryState) return [];
        const entry = recoveryStateMetadata.find(
            (m) => m.value === currentRecoveryState,
        );
        return entry?.allowedTransitions ?? [];
    }, [currentRecoveryState, recoveryStateMetadata]);

    const handleTransition = async (to: RecoveryState) => {
        setStateMenuAnchor(null);
        if (!invoiceId) return;
        const result = await dispatch(
            transitionInvoiceState({ invoiceId, to }),
        );
        if ((result as any).meta?.requestStatus === "fulfilled") {
            toast.success(`State changed to ${recoveryStateLabel(to)}.`);
            dispatch(fetchInvoice({ invoiceId }));
            dispatch(fetchInvoiceTransitions({ invoiceId }));
        } else {
            toast.error(
                (result as any).payload || "Couldn't change invoice state.",
            );
        }
    };

    const customerName =
        (invoice as any)?.customerDisplayName ||
        invoice?.customer?.displayName ||
        "Customer";

    const issueDate = invoice?.txnDate || invoice?.issueDate;

    const openSendDialog = () => {
        const def =
            templates.find((t) => t.category === "overdue" && t.isActive) ||
            templates.find((t) => t.channel === "email" && t.isActive) ||
            templates[0];
        if (def) setPickedTemplateId(def._id);
        setSuccessMessage("");
        setDialogOpen(true);
    };

    const pendingPromise = useMemo(
        () => promises.find((p) => p.status === "pending"),
        [promises],
    );
    const resolvedPromises = useMemo(
        () => promises.filter((p) => p.status !== "pending"),
        [promises],
    );

    const openPromiseDialog = () => {
        const remaining = invoice?.balance ?? 0;
        setPromiseAmount(remaining > 0 ? String(remaining) : "");
        const d = new Date();
        d.setDate(d.getDate() + 7);
        setPromiseDate(d.toISOString().slice(0, 10));
        setPromiseNote("");
        setPromiseDialogOpen(true);
    };

    const refreshAfterPromise = () => {
        if (!invoiceId) return;
        dispatch(fetchInvoicePromises({ invoiceId }));
        dispatch(fetchInvoice({ invoiceId }));
        dispatch(fetchInvoiceTransitions({ invoiceId }));
    };

    const onCreatePromise = async () => {
        if (!invoiceId) return;
        const amt = Number(promiseAmount);
        if (!amt || amt <= 0) {
            toast.error("Enter a valid promised amount.");
            return;
        }
        if (!promiseDate) {
            toast.error("Pick a promised date.");
            return;
        }
        const result = await dispatch(
            createInvoicePromise({
                invoiceId,
                promisedAmount: amt,
                promisedDate: promiseDate,
                ...(promiseNote ? { note: promiseNote } : {}),
            }),
        );
        if ((result as any).meta?.requestStatus === "fulfilled") {
            toast.success("Promise recorded.");
            setPromiseDialogOpen(false);
            refreshAfterPromise();
        } else {
            toast.error(
                (result as any).payload || "Couldn't record promise.",
            );
        }
    };

    const onResolvePromise = async (
        promiseId: string,
        kind: "kept" | "broken" | "cancel",
    ) => {
        const action =
            kind === "kept"
                ? markPromiseKept
                : kind === "broken"
                    ? markPromiseBroken
                    : cancelInvoicePromise;
        const result = await dispatch(action({ promiseId }));
        if ((result as any).meta?.requestStatus === "fulfilled") {
            toast.success(
                kind === "kept"
                    ? "Promise marked as kept."
                    : kind === "broken"
                        ? "Promise marked as broken."
                        : "Promise cancelled.",
            );
            refreshAfterPromise();
        } else {
            toast.error(
                (result as any).payload || "Couldn't update promise.",
            );
        }
    };

    const trimmedOverride = recipientOverride.trim();
    const isOverrideValidEmail =
        trimmedOverride === "" || EMAIL_REGEX.test(trimmedOverride);

    const onSend = async () => {
        if (!invoiceId || !pickedTemplateId) return;
        if (!isOverrideValidEmail) {
            toast.error("Enter a valid email address in the \u201CSend to\u201D field, or leave it blank.");
            return;
        }
        const result = await dispatch(
            sendManualReminder({
                invoiceId,
                templateId: pickedTemplateId,
                ...(trimmedOverride ? { recipientOverride: trimmedOverride } : {}),
            }),
        );
        if ((result as any).meta?.requestStatus === "fulfilled") {
            setSuccessMessage("Reminder sent.");
            setDialogOpen(false);
            toast.success("Reminder sent.");
            // Refresh timeline so the new event appears.
            dispatch(fetchInvoiceTimeline({ invoiceId }));
        } else {
            toast.error(
                (result as any).payload ||
                "Couldn't send reminder. Try a different template or recipient.",
            );
        }
    };

    if (!invoice) {
        return (
            <CollectPageWrapper>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 8,
                    }}
                >
                    <CircularProgress sx={{ color: colors.mainSecondary }} />
                </Box>
            </CollectPageWrapper>
        );
    }

    return (
        <CollectPageWrapper>

            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <CollectGhostButton
                    onClick={() => navigate("/collect/invoices")}
                    startIcon={<ArrowBack fontSize="small" />}
                    sx={{ mb: 2 }}
                >
                    Back to invoices
                </CollectGhostButton>

                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}
                {successMessage && (
                    <CollectInfoBanner tone="ok">
                        <span>{successMessage}</span>
                    </CollectInfoBanner>
                )}

                {/* Invoice header card */}
                <CollectSectionCard>
                    <FlexBetween>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 800,
                                    fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                    color: colors.primary,
                                }}
                            >
                                Invoice #{invoice.docNumber || "—"}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    color: "#6b7280",
                                    fontSize: "0.9rem",
                                    mt: 0.5,
                                }}
                            >
                                Issued to {customerName}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            <CollectStatusChip
                                label={invoice.status.replace("_", " ")}
                                statusKey={invoice.status as any}
                            />
                            {currentRecoveryState && (
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        padding: "4px 10px",
                                        borderRadius: "999px",
                                        backgroundColor: recoveryColors.bg,
                                        color: recoveryColors.fg,
                                        border: `1px solid ${recoveryColors.border}`,
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                        letterSpacing: "0.02em",
                                    }}
                                >
                                    <TimelineOutlined sx={{ fontSize: 14 }} />
                                    {recoveryStateLabel(currentRecoveryState)}
                                </Box>
                            )}
                            <CollectGhostButton
                                startIcon={<SwapHorizOutlined fontSize="small" />}
                                onClick={(e) =>
                                    setStateMenuAnchor(e.currentTarget as HTMLElement)
                                }
                                disabled={
                                    isTransitioning ||
                                    allowedTransitions.length === 0
                                }
                            >
                                {isTransitioning ? "Updating…" : "Change state"}
                            </CollectGhostButton>
                            <Menu
                                anchorEl={stateMenuAnchor}
                                open={Boolean(stateMenuAnchor)}
                                onClose={() => setStateMenuAnchor(null)}
                            >
                                {allowedTransitions.length === 0 && (
                                    <MenuItem disabled>
                                        No transitions available
                                    </MenuItem>
                                )}
                                {allowedTransitions.map((target) => (
                                    <MenuItem
                                        key={target.value}
                                        onClick={() => handleTransition(target.value)}
                                    >
                                        {target.label || recoveryStateLabel(target.value)}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <CollectPrimaryButton
                                startIcon={<MailOutline fontSize="small" />}
                                onClick={openSendDialog}
                                disabled={
                                    invoice.status === "paid" ||
                                    invoice.status === "void" ||
                                    templates.length === 0
                                }
                            >
                                Send reminder
                            </CollectPrimaryButton>
                        </Box>
                    </FlexBetween>

                    <Grid container spacing={2} sx={{ mt: 1.5 }}>
                        <SummaryItem
                            label="Total"
                            value={formatMoney(invoice.totalAmount, invoice.currency)}
                        />
                        <SummaryItem
                            label="Open balance"
                            value={formatMoney(invoice.balance, invoice.currency)}
                            highlight
                        />
                        <SummaryItem
                            label="Issued"
                            value={formatDate(issueDate)}
                        />
                        <SummaryItem
                            label="Due"
                            value={formatDate(invoice.dueDate)}
                        />
                        {typeof invoice.daysOverdue === "number" &&
                            invoice.daysOverdue > 0 && (
                                <SummaryItem
                                    label="Days overdue"
                                    value={`${invoice.daysOverdue}`}
                                    tone="bad"
                                />
                            )}
                    </Grid>
                </CollectSectionCard>

                {/* Line items */}
                <CollectSectionCard>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: colors.primary,
                            mb: 1.5,
                        }}
                    >
                        Line items
                    </Typography>
                    {invoice.lineItems && invoice.lineItems.length > 0 ? (
                        <CollectTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>Description</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">Qty</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">Unit price</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">Amount</CollectTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoice.lineItems.map((li, idx) => (
                                        <TableRow key={idx}>
                                            <CollectTableBodyCell>
                                                {li.description || "—"}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                {li.quantity ?? "—"}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                {li.unitPrice != null
                                                    ? formatMoney(li.unitPrice, invoice.currency)
                                                    : "—"}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <strong>
                                                    {formatMoney(li.amount, invoice.currency)}
                                                </strong>
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    ) : (
                        <CollectEmptyState>
                            <CollectEmptyHint>
                                No line items synced for this invoice.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    )}
                </CollectSectionCard>

                {/* Recovery state history */}
                <CollectSectionCard>
                    <FlexBetween style={{ marginBottom: 12 }}>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                color: colors.primary,
                            }}
                        >
                            <TimelineOutlined
                                fontSize="small"
                                sx={{ verticalAlign: "middle", mr: 1 }}
                            />
                            State history
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.8rem",
                                color: "#6b7280",
                            }}
                        >
                            Recovery state machine
                        </Typography>
                    </FlexBetween>

                    {isLoadingTransitions ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress
                                size={24}
                                sx={{ color: colors.mainSecondary }}
                            />
                        </Box>
                    ) : invoiceTransitions.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No state changes yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                State transitions are recorded automatically as the
                                invoice moves through monitoring, due, overdue, and
                                recovery stages.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {invoiceTransitions.map((t) => (
                                <StateTransitionRow key={t._id} transition={t} />
                            ))}
                        </Box>
                    )}
                </CollectSectionCard>

                {/* Promise-to-Pay */}
                <CollectSectionCard>
                    <FlexBetween style={{ marginBottom: 12 }}>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                color: colors.primary,
                            }}
                        >
                            Promises to pay
                        </Typography>
                        <CollectPrimaryButton
                            onClick={openPromiseDialog}
                            disabled={
                                isSavingPromise ||
                                Boolean(pendingPromise) ||
                                !invoice.balance ||
                                invoice.balance <= 0
                            }
                        >
                            {pendingPromise ? "Pending promise" : "Add promise"}
                        </CollectPrimaryButton>
                    </FlexBetween>

                    {isLoadingPromises ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress
                                size={24}
                                sx={{ color: colors.mainSecondary }}
                            />
                        </Box>
                    ) : promises.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No promises yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Record a customer's commitment to pay by a specific
                                date. The invoice will pause active recovery until
                                the promise is kept or broken.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {pendingPromise && (
                                <PromiseRow
                                    promise={pendingPromise}
                                    canResolve={!isSavingPromise}
                                    onResolve={(kind) =>
                                        onResolvePromise(pendingPromise._id, kind)
                                    }
                                />
                            )}
                            {resolvedPromises.map((p) => (
                                <PromiseRow key={p._id} promise={p} />
                            ))}
                        </Box>
                    )}
                </CollectSectionCard>

                {/* Stage 2F — communication opt-out warning so operators
                    know reminders to this customer are being suppressed. */}
                {(invoice?.customer?.communicationOptedOut ||
                    invoice?.customer?.smsOptedOut) && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This customer has opted out of
                            {invoice?.customer?.communicationOptedOut &&
                                invoice?.customer?.smsOptedOut
                                ? " all communications"
                                : invoice?.customer?.smsOptedOut
                                    ? " SMS"
                                    : " email"}
                            . Automated reminders on those channels will be
                            skipped.
                        </Alert>
                    )}

                {/* Unified activity feed (Phase 2E) */}
                {invoiceId && businessId && (
                    <InvoiceUnifiedActivity
                        businessId={businessId}
                        invoiceId={invoiceId}
                    />
                )}

                {/* Activity timeline */}
                <CollectSectionCard>
                    <FlexBetween style={{ marginBottom: 12 }}>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                color: colors.primary,
                            }}
                        >
                            <HistoryOutlined
                                fontSize="small"
                                sx={{ verticalAlign: "middle", mr: 1 }}
                            />
                            Activity
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.8rem",
                                color: "#6b7280",
                            }}
                        >
                            Reminders & QuickBooks events
                        </Typography>
                    </FlexBetween>

                    {isLoadingTimeline ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress
                                size={24}
                                sx={{ color: colors.mainSecondary }}
                            />
                        </Box>
                    ) : timeline.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No activity yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                When dunning reminders are sent or QuickBooks pushes
                                updates for this invoice, they'll show up here.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {timeline.map((entry, idx) => (
                                <TimelineRow key={idx} entry={entry} />
                            ))}
                        </Box>
                    )}
                </CollectSectionCard>

                {/* Customer replies for this invoice */}
                {invoiceId && businessId && (
                    <InvoiceRepliesSection
                        businessId={businessId}
                        invoiceId={invoiceId}
                        onOpenInbox={() => navigate("/collect/replies")}
                    />
                )}
            </Box>

            {/* Send-reminder dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                >
                    Send a reminder
                </DialogTitle>
                <DialogContent>
                    {sendError && (
                        <CollectInfoBanner tone="warn">
                            <span>{sendError}</span>
                        </CollectInfoBanner>
                    )}
                    <TextField
                        select
                        fullWidth
                        label="Template"
                        margin="normal"
                        value={pickedTemplateId}
                        onChange={(e) => setPickedTemplateId(e.target.value)}
                    >
                        {templates.length === 0 && (
                            <MenuItem value="" disabled>
                                No templates available
                            </MenuItem>
                        )}
                        {templates.map((t: IReminderTemplate) => (
                            <MenuItem key={t._id} value={t._id}>
                                {t.name} ({t.channel.toLowerCase()})
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        type="email"
                        label="Send to (override)"
                        placeholder={
                            invoice.customer?.email || "Defaults to customer email on file"
                        }
                        margin="normal"
                        value={recipientOverride}
                        onChange={(e) => setRecipientOverride(e.target.value)}
                        error={!isOverrideValidEmail}
                        helperText={
                            !isOverrideValidEmail
                                ? "Enter a valid email address (e.g. name@company.com) or leave blank."
                                : "Leave blank to use the email on the customer record."
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setDialogOpen(false)}>
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={onSend}
                        disabled={!pickedTemplateId || isSending || !isOverrideValidEmail}
                        startIcon={
                            isSending ? (
                                <CircularProgress size={14} sx={{ color: "#fff" }} />
                            ) : (
                                <MailOutline fontSize="small" />
                            )
                        }
                    >
                        {isSending ? "Sending…" : "Send now"}
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>

            {/* Promise dialog */}
            <Dialog
                open={promiseDialogOpen}
                onClose={() => setPromiseDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                >
                    Record a promise to pay
                </DialogTitle>
                <DialogContent>
                    {promiseSaveError && (
                        <CollectInfoBanner tone="warn">
                            <span>{promiseSaveError}</span>
                        </CollectInfoBanner>
                    )}
                    <TextField
                        fullWidth
                        type="number"
                        label={`Promised amount (${invoice.currency || "USD"})`}
                        margin="normal"
                        value={promiseAmount}
                        onChange={(e) => setPromiseAmount(e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        helperText={`Open balance: ${formatMoney(invoice.balance, invoice.currency)}`}
                    />
                    <TextField
                        fullWidth
                        type="date"
                        label="Promised date"
                        margin="normal"
                        value={promiseDate}
                        onChange={(e) => setPromiseDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Note (optional)"
                        margin="normal"
                        value={promiseNote}
                        onChange={(e) => setPromiseNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton
                        onClick={() => setPromiseDialogOpen(false)}
                    >
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={onCreatePromise}
                        disabled={isSavingPromise}
                        startIcon={
                            isSavingPromise ? (
                                <CircularProgress
                                    size={14}
                                    sx={{ color: "#fff" }}
                                />
                            ) : undefined
                        }
                    >
                        {isSavingPromise ? "Saving…" : "Record promise"}
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

// ── Promise row helper ────────────────────────────────────────────────────
const PromiseRow: React.FC<{
    promise: IPromiseToPay;
    canResolve?: boolean;
    onResolve?: (kind: "kept" | "broken" | "cancel") => void;
}> = ({ promise, canResolve, onResolve }) => {
    const statusColors: Record<string, { bg: string; fg: string; border: string }> = {
        pending: { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
        kept: { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
        broken: { bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
        cancelled: { bg: "#f3f4f6", fg: "#4b5563", border: "#d1d5db" },
    };
    const c = statusColors[promise.status] || statusColors.cancelled;
    const creator =
        typeof promise.createdByUserId === "object" && promise.createdByUserId
            ? `${(promise.createdByUserId as any).firstName ?? ""} ${(promise.createdByUserId as any).lastName ?? ""}`.trim() || (promise.createdByUserId as any).email
            : null;
    return (
        <Box
            sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "12px 14px",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                alignItems: { sm: "center" },
                justifyContent: "space-between",
                backgroundColor: "#fff",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: "#111827",
                        }}
                    >
                        {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: promise.currency || "USD",
                        }).format(promise.promisedAmount)}
                    </Typography>
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            backgroundColor: c.bg,
                            color: c.fg,
                            border: `1px solid ${c.border}`,
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                        }}
                    >
                        {promise.status}
                    </Box>
                </Box>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.82rem",
                        color: "#6b7280",
                    }}
                >
                    Promised for{" "}
                    {new Date(promise.promisedDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        timeZone: "UTC",
                    })}
                    {promise.gracePeriodDays
                        ? ` · ${promise.gracePeriodDays}-day grace`
                        : ""}
                    {creator ? ` · by ${creator}` : ` · source: ${promise.source}`}
                </Typography>
                {promise.note && (
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.82rem",
                            color: colors.mainSecondary,
                            // fontStyle: "italic",
                        }}
                    >
                        “{humanizePromiseNote(promise.note)}”
                    </Typography>
                )}
                {promise.resolutionReason && (
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.78rem",
                            color: "#6b7280",
                        }}
                    >
                        Reason: {promise.resolutionReason}
                    </Typography>
                )}
            </Box>
            {promise.status === "pending" && onResolve && (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    <CollectGhostButton
                        size="small"
                        onClick={() => onResolve("kept")}
                        disabled={!canResolve}
                    >
                        Mark kept
                    </CollectGhostButton>
                    <CollectGhostButton
                        size="small"
                        onClick={() => onResolve("broken")}
                        disabled={!canResolve}
                    >
                        Mark broken
                    </CollectGhostButton>
                    <CollectGhostButton
                        size="small"
                        onClick={() => onResolve("cancel")}
                        disabled={!canResolve}
                    >
                        Cancel
                    </CollectGhostButton>
                </Box>
            )}
        </Box>
    );
};

// ── Small helpers ─────────────────────────────────────────────────────────

const SummaryItem: React.FC<{
    label: string;
    value: string;
    highlight?: boolean;
    tone?: "bad" | "ok";
}> = ({ label, value, highlight, tone }) => (
    <Grid item xs={6} sm={3}>
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.5,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: highlight ? "1.4rem" : "1.05rem",
                fontWeight: highlight ? 800 : 600,
                color:
                    tone === "bad"
                        ? "#b91c1c"
                        : highlight
                            ? colors.primary
                            : "#1f2937",
            }}
        >
            {value}
        </Typography>
    </Grid>
);

const StateTransitionRow: React.FC<{ transition: IRecoveryStateTransition }> = ({
    transition,
}) => {
    const toColors = recoveryStateColors(transition.toState);
    const fromColors = recoveryStateColors(
        transition.fromState ?? undefined,
    );
    const actor =
        transition.actorUserId && typeof transition.actorUserId === "object"
            ? `${transition.actorUserId.firstName ?? ""} ${transition.actorUserId.lastName ?? ""}`.trim() ||
            transition.actorUserId.email ||
            "User"
            : transition.actorType === "system"
                ? "System"
                : transition.actorType === "webhook"
                    ? "Webhook"
                    : transition.actorType === "ai"
                        ? "AI"
                        : "User";

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
                padding: "10px 14px",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {transition.fromState && (
                    <>
                        <Box
                            sx={{
                                padding: "2px 8px",
                                borderRadius: "999px",
                                backgroundColor: fromColors.bg,
                                color: fromColors.fg,
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                            }}
                        >
                            {recoveryStateLabel(transition.fromState)}
                        </Box>
                        <Typography sx={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                            →
                        </Typography>
                    </>
                )}
                <Box
                    sx={{
                        padding: "2px 8px",
                        borderRadius: "999px",
                        backgroundColor: toColors.bg,
                        color: toColors.fg,
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                    }}
                >
                    {recoveryStateLabel(transition.toState)}
                </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.82rem",
                        color: "#374151",
                    }}
                >
                    {transitionSourceLabel(transition.source)} · {actor}
                </Typography>
                {transition.reason && (
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.78rem",
                            color: colors.mainSecondary,
                            // fontStyle: "italic",
                            mt: 0.25,
                        }}
                    >
                        "{transition.reason}"
                    </Typography>
                )}
            </Box>
            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "0.78rem",
                    color: "#6b7280",
                }}
            >
                {formatDateTime(transition.createdAt)}
            </Typography>
        </Box>
    );
};

const TimelineRow: React.FC<{ entry: IInvoiceTimelineEntry }> = ({ entry }) => {
    const isReminder = entry.kind === "dunning";
    const dunning = isReminder ? (entry.data as IDunningEvent) : null;
    const webhook = !isReminder ? (entry.data as any) : null;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1.5,
                padding: "12px 14px",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
            }}
        >
            <Box sx={{ pt: 0.3 }}>
                {dunning
                    ? TIMELINE_ICONS[(dunning.status || "").toLowerCase()] || TIMELINE_ICONS.sent
                    : TIMELINE_ICONS.sent}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: colors.primary,
                    }}
                >
                    {dunning
                        ? `${(dunning.trigger || "").toLowerCase() === "manual" ? "Manual" : "Scheduled"} reminder ${(dunning.status || "").toLowerCase()}`
                        : `QuickBooks event · ${webhook?.type || webhook?.eventType || "update"}`}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                    }}
                >
                    {formatDateTime(entry.occurredAt)}
                    {dunning?.recipient ? ` · ${dunning.recipient}` : ""}
                    {dunning?.channel ? ` · ${dunning.channel.toLowerCase()}` : ""}
                </Typography>
                {dunning?.subject && (
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.85rem",
                            color: colors.mainSecondary,
                            mt: 0.5,
                            // fontStyle: "italic",
                        }}
                    >
                        "{dunning.subject}"
                    </Typography>
                )}
                {dunning?.error && (
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.78rem",
                            color: "#b91c1c",
                            mt: 0.5,
                        }}
                    >
                        Error: {dunning.error}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default CollectInvoiceDetail;

// ─────────────────────────────────────────────────────────────────────────
// InvoiceRepliesSection — lightweight, self-contained read-only view of
// inbound customer replies linked to this invoice. Fetches directly from
// the service so we don't pollute the main /collect/replies inbox state.
// ─────────────────────────────────────────────────────────────────────────

const INTENT_TONE: Record<string, { bg: string; fg: string }> = {
    payment_commitment: { bg: "rgba(30,193,149,0.15)", fg: "#0f7a5b" },
    already_paid: { bg: "rgba(30,193,149,0.15)", fg: "#0f7a5b" },
    dispute: { bg: "rgba(239,68,68,0.15)", fg: "#b91c1c" },
    complaint: { bg: "rgba(245,158,11,0.18)", fg: "#92400e" },
    question: { bg: "rgba(59,130,246,0.12)", fg: "#1e40af" },
    unsubscribe: { bg: "rgba(107,114,128,0.18)", fg: "#374151" },
    auto_response: { bg: "rgba(107,114,128,0.12)", fg: "#4b5563" },
    other: { bg: "rgba(107,114,128,0.12)", fg: "#4b5563" },
};

const InvoiceRepliesSection: React.FC<{
    businessId: string;
    invoiceId: string;
    onOpenInbox: () => void;
}> = ({ businessId, invoiceId, onOpenInbox }) => {
    const [replies, setReplies] = useState<ICustomerReply[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string>("");

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setLoadError("");
        submitListReplies(businessId, { invoiceId, limit: 10 })
            .then((page) => {
                if (cancelled) return;
                setReplies(page?.items || []);
            })
            .catch((err: any) => {
                if (cancelled) return;
                setLoadError(err?.message || "Couldn't load replies.");
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [businessId, invoiceId]);

    return (
        <CollectSectionCard>
            <FlexBetween style={{ marginBottom: 12 }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        color: colors.primary,
                    }}
                >
                    <MarkEmailReadOutlined
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mr: 1 }}
                    />
                    Customer replies
                </Typography>
                <CollectGhostButton
                    onClick={onOpenInbox}
                    startIcon={<OpenInNewOutlined fontSize="small" />}
                >
                    Open inbox
                </CollectGhostButton>
            </FlexBetween>

            {loadError && (
                <CollectInfoBanner tone="warn">
                    <span>{loadError}</span>
                </CollectInfoBanner>
            )}

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress
                        size={24}
                        sx={{ color: colors.mainSecondary }}
                    />
                </Box>
            ) : replies.length === 0 ? (
                <CollectEmptyState>
                    <CollectEmptyTitle>No replies yet</CollectEmptyTitle>
                    <CollectEmptyHint>
                        When this customer replies to a reminder for this invoice,
                        the message will show up here with its detected intent.
                    </CollectEmptyHint>
                </CollectEmptyState>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {replies.map((reply) => (
                        <InvoiceReplyRow key={reply._id} reply={reply} />
                    ))}
                </Box>
            )}
        </CollectSectionCard>
    );
};

const InvoiceReplyRow: React.FC<{ reply: ICustomerReply }> = ({ reply }) => {
    const intent = reply.classification?.intent;
    const tone = (intent && INTENT_TONE[intent]) || INTENT_TONE.other;
    const preview = (reply.bodyText || reply.rawSnippet || "").slice(0, 280);
    const truncated = (reply.bodyText || "").length > 280;
    const confidence = reply.classification?.confidence;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                padding: "12px 14px",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                <ChannelBadge channel={reply.channel} />
                {intent && (
                    <Box
                        component="span"
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            padding: "3px 8px",
                            borderRadius: "999px",
                            backgroundColor: tone.bg,
                            color: tone.fg,
                        }}
                    >
                        {REPLY_INTENT_LABELS[intent]}
                        {typeof confidence === "number"
                            ? ` · ${Math.round(confidence * 100)}%`
                            : ""}
                    </Box>
                )}
                <CollectStatusChip
                    size="small"
                    label={REPLY_STATUS_LABELS[reply.status] || reply.status}
                />
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                        ml: "auto",
                    }}
                >
                    {formatDateTime(reply.receivedAt)}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "0.85rem",
                    color: "#374151",
                }}
            >
                <strong>From:</strong> {reply.fromAddress}
                {reply.subject ? ` · ${reply.subject}` : ""}
            </Typography>
            {preview && (
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "0.85rem",
                        color: "#1f2937",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {preview}
                    {truncated ? "…" : ""}
                </Typography>
            )}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────
// InvoiceUnifiedActivity (Phase 2E) — single source of truth feed pulling
// from the CollectActivity store: state transitions, reminders, replies,
// promises, payments, manual review actions, tasks, etc.
// ─────────────────────────────────────────────────────────────────────────

const ACTIVITY_SEVERITY_COLORS: Record<string, string> = {
    info: "#0ea5e9",
    success: "#16a34a",
    warning: "#f59e0b",
    critical: "#dc2626",
};

const ACTIVITY_ACTOR_ICONS: Record<string, string> = {
    user: "👤",
    system: "⚙️",
    ai: "🤖",
    webhook: "🌐",
    customer: "✉️",
};

function relativeTime(iso?: string): string {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "";
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso;
    }
}

const InvoiceUnifiedActivity: React.FC<{
    businessId: string;
    invoiceId: string;
}> = ({ businessId, invoiceId }) => {
    const [items, setItems] = useState<IActivityItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [err, setErr] = useState<string>("");

    const load = React.useCallback(async () => {
        setIsLoading(true);
        setErr("");
        try {
            const page = await fetchInvoiceActivity(businessId, invoiceId, {
                limit: 50,
            });
            setItems(page.items || []);
        } catch (e: any) {
            setErr(e?.message || "Could not load activity");
        } finally {
            setIsLoading(false);
        }
    }, [businessId, invoiceId]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <CollectSectionCard>
            <FlexBetween style={{ marginBottom: 12 }}>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        color: colors.primary,
                    }}
                >
                    <HistoryOutlined
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mr: 1 }}
                    />
                    Everything that happened
                </Typography>
                <CollectGhostButton onClick={load} disabled={isLoading}>
                    {isLoading ? "Loading…" : "Refresh"}
                </CollectGhostButton>
            </FlexBetween>

            {isLoading && items.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress
                        size={24}
                        sx={{ color: colors.mainSecondary }}
                    />
                </Box>
            ) : err ? (
                <CollectEmptyState>
                    <CollectEmptyTitle>
                        Couldn&apos;t load activity
                    </CollectEmptyTitle>
                    <CollectEmptyHint>{err}</CollectEmptyHint>
                </CollectEmptyState>
            ) : items.length === 0 ? (
                <CollectEmptyState>
                    <CollectEmptyTitle>Nothing yet</CollectEmptyTitle>
                    <CollectEmptyHint>
                        As reminders go out, customers reply, payments arrive
                        and reviewers take action, the full story will show up
                        here.
                    </CollectEmptyHint>
                </CollectEmptyState>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                    }}
                >
                    {items.map((a) => {
                        const color =
                            ACTIVITY_SEVERITY_COLORS[a.severity] || "#64748b";
                        const icon =
                            ACTIVITY_ACTOR_ICONS[a.actorType] || "•";
                        return (
                            <Box
                                key={a._id}
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    p: 1.25,
                                    borderRadius: "10px",
                                    border: "1px solid #e2e8f0",
                                    backgroundColor: "#fff",
                                    borderLeft: `3px solid ${color}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        fontSize: "1.1rem",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {icon}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <FlexBetween>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                flexWrap: "wrap",
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "0.85rem",
                                                    color: "#0f172a",
                                                }}
                                            >
                                                {a.title}
                                            </Typography>
                                            <ChannelBadge
                                                channel={a.metadata?.channel}
                                                size="xs"
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontSize: "0.72rem",
                                                color: "#64748b",
                                                ml: 1,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {relativeTime(a.occurredAt)}
                                        </Typography>
                                    </FlexBetween>
                                    {a.description && (
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontSize: "0.78rem",
                                                color: "#475569",
                                                mt: 0.25,
                                                whiteSpace: "pre-wrap",
                                            }}
                                        >
                                            {a.description}
                                        </Typography>
                                    )}
                                    {a.actorDisplayName && (
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontSize: "0.7rem",
                                                color: "#94a3b8",
                                                mt: 0.25,
                                            }}
                                        >
                                            by {a.actorDisplayName}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </CollectSectionCard>
    );
};
